import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";

import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import CommonLayout from "../../Common/Layout";
import { TextComponent } from "../../Common/Tile";
import { showToast, syncNotifications } from "../../../store/actions";
import DataSourceClass from "../../../service/dataSource";
import { ButtonUI } from "../../Common";
import ConfigHistory from "./ConfigHistory";
import ApplyDwDialog from "./ApplyToDWDialog";
import InvalidConnectionPopup from "./InvalidConnectionPopup";
import Toolbar from "../../Common/Toolbar";

const useStyles = makeStyles()((theme) => ({
  PanelContainer: {
    padding: "10px 30px 30px 30px",
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  loadingAnimation: {
    animation: "spin 2s ease-in-out infinite -1.5s",
  },
  [theme.breakpoints.down("961")]: {
    statusContainer: {
      marginTop: "4px",
      marginBottom: "12px",
      width: "100%",
      justifyContent: "space-between",
    },
  },
}));

const ApplyToDataWarehouse = () => {
  const { Workspace_id } = useParams();

  const workspace = useSelector((state) => state.workspace);
  const { Name: workspaceName, accessRole } = workspace.workspace;

  const [loading, setLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [configStatus, setConfigStatus] = useState("Modified");
  const [timeoutId, setTimeoutId] = useState(null);
  const [componentInUse, setComponentInUse] = useState(true);

  const [resetHistory, setResetHistory] = useState(false);

  const [openInvalidConfig, setOpenInvalidConfig] = useState({
    open: false,
    reservedSources: [],
    reservedEntities: [],
    IsDeletedInvalidSources: [],
    conflictingNames: [],
  });

  // State to use in selection popup
  const [modifiedObjects, setModifiedObjects] = useState({ sources: [], entites: [] });
  const [openSelection, setOpenSelection] = useState(false);
  const [applyAll, setApplyAll] = useState(true);
  const [openConfirm, setOpenConfirm] = useState({
    columnDeletion: false,
    tableDeletion: false,
  });
  const [selectionApplyConfirmation, setSelectiveApplyConfirmation] = useState({
    sources: [],
    entities: [],
  });

  const { classes } = useStyles();
  const dispatch = useDispatch();

  const selectiveApply = useRef({
    selectedSources: [],
    selectedEntities: [],
    unselectedSources: [],
    unselectedEntities: [],
  });

  // Create snapshot
  const createSnapshot = useCallback(async () => {
    setLoading(true);
    const response = await new DataSourceClass().createSnapshot(Workspace_id);
    if (response.success) {
      dispatch(
        showToast({
          message: response.message,
          type: "success",
        })
      );
      setResetHistory(true);
    }
    setLoading(false);
  }, [Workspace_id, dispatch, setLoading]);

  const openSelectionPopup = useCallback(async () => {
    setLoading(true);
    const datasource = new DataSourceClass();
    const { data, success } = await datasource.getModifiedObjects(Workspace_id);
    if (!success) {
      setLoading(false);
      return;
    }
    const { sources, entities } = JSON.parse(data);
    setModifiedObjects({
      sources: sources.map((object) => ({ ...object, checked: true })),
      entities: entities.map((object) => ({ ...object, checked: true })),
    });
    setOpenSelection(true);
    setLoading(false);
  }, [Workspace_id, setLoading]);

  const validateConfiguration = async () => {
    setConfirmLoading(true);
    const dataSource = new DataSourceClass();

    //Checking if every datasources has at least one datavault key
    const isValidConfigResponse = await dataSource.isValidConfig(Workspace_id, {
      applyAll,
      ...(!applyAll
        ? {
            selectedSources: selectiveApply.current.selectedSources,
            selectedEntities: selectiveApply.current.selectedEntities,
            unselectedSources: selectiveApply.current.unselectedSources,
            unselectedEntities: selectiveApply.current.unselectedEntities,
          }
        : {}),
    });

    if (isValidConfigResponse.success && !isValidConfigResponse.data.isValidConfig) {
      setOpenInvalidConfig({
        open: true,
        reservedSources: isValidConfigResponse.data.reservedSources,
        reservedEntities: isValidConfigResponse.data.reservedEntities,
        IsDeletedInvalidSources: isValidConfigResponse.data.IsDeletedInvalidSources,
        conflictingNames: isValidConfigResponse.data.conflictingNames,
      });
      setConfirmLoading(false);
      setOpenSelection(false);
      return;
    }

    //Checking if anything will be deleted from snowflake after applying to datawarehouse
    const response = await dataSource.regenerateModel(Workspace_id, {
      applyAll: true,
      ...(!applyAll
        ? {
            unselectedSources: selectiveApply.current.unselectedSources,
            unselectedEntities: selectiveApply.current.unselectedEntities,
          }
        : {}),
    });

    if (response.success)
      setOpenConfirm({ columnDeletion: response.data.columnDeletion, tableDeletion: response.data.tableDeletion });

    setConfirmLoading(false);
  };

  const handleApplySelection = async () => {
    if (applyAll) await validateConfiguration();
    else {
      setConfirmLoading(true);
      const dataSource = new DataSourceClass();

      // Finding conflicting objects and confirming with user to apply all of them at once
      const { success, data } = await dataSource.getConflictingObjects(Workspace_id, {
        sources: modifiedObjects.sources.filter(({ checked }) => checked),
        entities: modifiedObjects.entities.filter(({ checked }) => checked),
      });

      if (success) {
        const {
          selectedSources = [],
          selectedEntities = [],
          unselectedSources = [],
          unselectedEntities = [],
        } = JSON.parse(data);

        selectiveApply.current = {
          applyAll: false,
          selectedSources: selectedSources.map(({ id }) => id),
          selectedEntities: selectedEntities.map(({ id }) => id),
          unselectedSources: unselectedSources.map(({ id }) => id),
          unselectedEntities: unselectedEntities.map(({ id }) => id),
        };
        setSelectiveApplyConfirmation({
          open: true,
          sources: selectedSources,
          entities: selectedEntities,
        });
      }

      setConfirmLoading(false);
    }
  };

  const handleSelectionPopup = useCallback(() => {
    setOpenSelection((prev) => !prev);
  }, []);

  const handlePushToWarehouse = async () => {
    setOpenSelection(false);

    setLoading(true);

    const dataSource = new DataSourceClass();
    const response = await dataSource.syncData(Workspace_id, {
      applyAll,
      ...(!applyAll
        ? {
            selectedSources: selectiveApply.current.selectedSources,
            selectedEntities: selectiveApply.current.selectedEntities,
            unselectedSources: selectiveApply.current.unselectedSources,
            unselectedEntities: selectiveApply.current.unselectedEntities,
          }
        : {}),
    });

    setLoading(false);

    if (response?.message) {
      dispatch(
        showToast({
          message: response.message,
          type: response.success ? "success" : "error",
        })
      );
      setConfigStatus(response?.status || "Modified");
      setTimeout(() => {
        updateStatus(response?.status || "Modified");
      }, 0);
    }

    setSelectiveApplyConfirmation({ sources: [], entities: [], open: false });
  };

  // Update Status of Configuration and Notification
  const updateStatus = useCallback(
    async (configStatus, resetHistory) => {
      if (resetHistory) setResetHistory(true);

      const response = await new DataSourceClass().getConfigStatus(Workspace_id);
      if (response.success) {
        if (!componentInUse) return;
        let currentConfigStatus = response.data.Status;

        // For updating our current page as per the new notifications
        if (currentConfigStatus !== configStatus) {
          setConfigStatus(currentConfigStatus);
          if (!resetHistory) setResetHistory(true);

          if (configStatus === "Applying" || configStatus === "Processing Data")
            dispatch(syncNotifications({ sync: true }));
        }

        if (currentConfigStatus === "Applying" || currentConfigStatus === "Processing Data") {
          const id = setTimeout(() => updateStatus(currentConfigStatus), 20000);
          setTimeoutId(id);
        }
      }
    },
    [Workspace_id, componentInUse, dispatch]
  );

  useEffect(() => {
    return () => setComponentInUse(false);
  }, []);

  useEffect(() => {
    updateStatus(configStatus, true);
    // Adding dependancy will call API twice
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup the timeout if the component is unmounted
  useEffect(() => {
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [timeoutId]);

  useEffect(() => {
    selectiveApply.current = {
      applyAll: false,
      selectedSources: [],
      selectedEntities: [],
      unselectedSources: [],
      unselectedEntities: [],
    };
  }, [openSelection]);

  const breadcrumbArr = useMemo(
    () => [
      { link: "/my-workspaces", displayName: "Workspaces", active: false },
      { link: `/my-workspaces/${Workspace_id}`, displayName: workspaceName, active: false },
      { link: "#", displayName: "Apply to Data Warehouse", active: true },
    ],
    [Workspace_id, workspaceName]
  );

  return (
    <CommonLayout title="Apply to Data Warehouse" breadcrumbArr={breadcrumbArr}>
      <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="primary" />
      </Backdrop>
      <Container maxWidth="false" sx={{ m: 0, height: "100%", overflowY: "auto" }} className={classes.PanelContainer}>
        <Toolbar title="Configuration History" helpText="Apply to Data Warehouse">
          <Box sx={{ display: "inline-flex", alignItems: "center", mb: 1 }} className={classes.statusContainer}>
            <Box sx={{ mr: { xs: 0, sm: 1, md: 2 }, whiteSpace: "nowrap" }}>
              <TextComponent
                label="Running Config"
                style={{ width: "100%", fontSize: "14px" }}
                value={
                  configStatus.toLowerCase() === "applying" ? (
                    <Box
                      fontWeight={"bold"}
                      color={(theme) =>
                        configStatus === "Applied" ? theme.palette.success.main : theme.palette.error.main
                      }
                      sx={{ display: "inline-flex", alignItems: "center" }}
                    >
                      {configStatus}
                      <AutorenewIcon
                        sx={{ fontSize: 18, m: "2px 0 0 2px" }}
                        color="info"
                        className={classes.loadingAnimation}
                      />
                    </Box>
                  ) : (
                    <Box
                      fontWeight={"bold"}
                      color={(theme) =>
                        configStatus === "Applied" ? theme.palette.success.main : theme.palette.error.main
                      }
                      display="inline-flex"
                    >
                      {configStatus}
                    </Box>
                  )
                }
              />
            </Box>
            <Box sx={{ whiteSpace: "nowrap" }}>
              {[1, 2].includes(accessRole) ? (
                <ButtonUI
                  variant="contained"
                  onClick={openSelectionPopup}
                  disabled={["applied", "applying", "processing data"].includes(configStatus.toLowerCase())}
                  color="success"
                  sx={{ textTransform: "inherit !important", mr: { xs: 0, sm: 1, md: 2 } }}
                  className={classes.btnMt10}
                >
                  Apply to Data Warehouse
                </ButtonUI>
              ) : null}
              <ButtonUI
                variant="contained"
                color="info"
                onClick={createSnapshot}
                sx={{ textTransform: "inherit !important", mr: "5px" }}
              >
                Create Snapshot
              </ButtonUI>
              <Tooltip placement="top" title="Refresh" arrow>
                <span>
                  <IconButton
                    disabled={loading || confirmLoading || resetHistory}
                    onClick={() => updateStatus(configStatus, true)}
                  >
                    <RefreshOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Toolbar>
        <ConfigHistory resetHistory={resetHistory} setResetHistory={setResetHistory} WorkspaceName={workspaceName} />
      </Container>
      {openInvalidConfig.open && (
        <InvalidConnectionPopup
          reservedSources={openInvalidConfig.reservedSources}
          reservedEntities={openInvalidConfig.reservedEntities}
          IsDeletedInvalidSources={openInvalidConfig.IsDeletedInvalidSources}
          conflictingNames={openInvalidConfig.conflictingNames}
          closePopup={() => {
            setOpenInvalidConfig({
              open: false,
            });
            setSelectiveApplyConfirmation({
              open: false,
              sources: [],
              entities: [],
            });
          }}
        />
      )}

      {openSelection && (
        <ApplyDwDialog
          applyAll={applyAll}
          setApplyAll={setApplyAll}
          selectionApplyConfirmation={selectionApplyConfirmation}
          setSelectiveApplyConfirmation={setSelectiveApplyConfirmation}
          modifiedObjects={modifiedObjects}
          setModifiedObjects={setModifiedObjects}
          openConfirm={openConfirm}
          setOpenConfirm={setOpenConfirm}
          handleSelectionPopup={handleSelectionPopup}
          handleFirstStep={handleApplySelection}
          handleSecondStep={validateConfiguration}
          handleApply={handlePushToWarehouse}
          loading={confirmLoading}
        />
      )}
    </CommonLayout>
  );
};

export default React.memo(ApplyToDataWarehouse);
